import * as THREE from 'three'
import {
  types,
  getParent,
  Instance,
  SnapshotIn,
  getSnapshot,
  IJsonPatch
} from 'mobx-state-tree'
import {
  default as referencePoints,
  centerCenter,
  ReferencePoint
} from 'src/constants/referencePoints'
import {
  coverPlate,
  elementEdge,
  printedcircuitboard
} from 'src/constants/typenames'
import { getReferencePoints2D } from 'src/utils/getReferencePoints'
import { Translation } from 'src/utils/getTranslation'
import { IPrintedcircuitboard } from '../../Content/Printedcircuitboard/types'
import { IAdapterPlate } from '../../Enclosure/Adapter/types'
import { ICoverPlateEdge, ICoverPlate } from '../../Enclosure/Cover/types'
import {
  IOffset,
  IElementEdge,
  IReferencePoint,
  IElementEdgeProperties,
  IReference
} from './types'
import {
  generateValueTypeWithLimits,
  generateEnumValue
} from 'src/api/models/Values/index'
import { back, bottom, front, left, right, top } from 'src/constants/sides'
import { IElement } from '../Element/types'
import { IJsonPatchInput } from 'src/api/misc/Drafter/types'

const OffsetX = generateValueTypeWithLimits({
  min: -Infinity
  // getRange: self => {
  //   const width = getParentOfType(self, types.late(CoverPlate)).properties.width.value
  //   return { min: width / -2, max: width / 2 }
  // }
})

const OffsetY = generateValueTypeWithLimits({
  min: -Infinity
  // getRange: self => {
  //   const length = getParentOfType(self, CoverPlate()).properties.length.value
  //   return { min: length / -2, max: length / 2 }
  // }
})

const Offset: IOffset = types
  .model('Offset', {
    x: OffsetX,
    y: OffsetY
  })
  .actions((self) => ({
    /**
     * @params [patch, reversePatch]
     * @param path Get path from middleware
     */
    patch(
      [change, reverseChange]: [IJsonPatchInput, IJsonPatchInput],
      patch: boolean | undefined = undefined,
      path?: string
    ) {
      if (path) {
        return [
          { op: 'replace', path, value: change },
          { op: 'replace', path, value: reverseChange }
        ]
      }
    },
    update({ x, y }: { [key in 'x' | 'y']: number }, patch: boolean = true) {
      const nextOffset = {
        x: { ...self.x, value: Number(x) },
        y: { ...self.y, value: Number(y) }
      }
      getParent<Instance<IElementEdgeProperties>>(self).updateOffset(
        nextOffset,
        patch
      )
    }
  }))

const ReferencePointModel = generateEnumValue([...referencePoints]).views(
  () => ({
    get type() {
      return 'select' as const
    }
  })
)

const ParentReferencePoint: IReferencePoint = ReferencePointModel.actions(
  (self) => ({
    update(value: ReferencePoint) {
      // 2. ElementEdge 1. ElementEdgeProperties
      const elementEdge = getParent<Instance<IElementEdge>>(self, 2)
      const [
        currX,
        currY
      ] = elementEdge.properties.getParentReferencePointCoordinates(self.value) //referencePoints[self.value as ReferencePoint]
      const [
        nextX,
        nextY
      ] = elementEdge.properties.getParentReferencePointCoordinates(value) //referencePoints[value]
      const reverseChange = getSnapshot(elementEdge.properties)
      const [dX, dY] = [currX - nextX, currY - nextY] as [number, number]
      const offset = {
        x: {
          ...reverseChange.offset.x,
          value: reverseChange.offset.x.value + dX
        },
        y: {
          ...reverseChange.offset.y,
          value: reverseChange.offset.y.value + dY
        }
      }
      const change = {
        ...reverseChange,
        parentReferencePoint: { value },
        offset
      }

      /** update values */
      self.value = value
      elementEdge.properties.offset.update(
        {
          x: offset.x.value,
          y: offset.y.value
        },
        false
      )

      // /** register Patch in DraftStore */
      elementEdge.properties.patch([change, reverseChange])

      /** change volatiles */
      elementEdge.reference?.value?.focusReferencePoint?.({
        referencePoint: value,
        side: elementEdge.side
      })
    }
  })
)

const ElementReferencePoint: IReferencePoint = ReferencePointModel.actions(
  (self) => ({
    update(value: ReferencePoint, patch = false) {
      const elementEdge = getParent<Instance<IElementEdge>>(self, 2)
      const referencePoints = elementEdge.node.getReferencePoints()

      /** prepare Patch */
      const [currX, currY] = referencePoints[self.value as ReferencePoint]
      const [nextX, nextY] = referencePoints[value]
      const reverseChange = getSnapshot(elementEdge.properties)
      const [dX, dY] = [currX - nextX, currY - nextY] as [number, number]
      const offset = {
        x: {
          ...reverseChange.offset.x,
          value: reverseChange.offset.x.value + dX
        },
        y: {
          ...reverseChange.offset.y,
          value: reverseChange.offset.y.value + dY
        }
      }
      const change = {
        ...reverseChange,
        elementReferencePoint: { value },
        offset
      }

      /** update values */
      self.value = value
      elementEdge.properties.offset.update(
        {
          x: offset.x.value,
          y: offset.y.value
        },
        false
      )

      /** register Patch in DraftStore */
      elementEdge.properties.patch([change, reverseChange])

      /** change volatiles */
      elementEdge.node.focusReferencePoint?.({
        referencePoint: value,
        side: elementEdge.side
      })
    }
  })
)

/**
 *
 */

const getElementSize = (
  element: Instance<IElement>
): { width: number | Promise<number>; height: number | Promise<number> } => {
  switch (element.__typename) {
    case 'RectangularClippingElement':
    case 'RectangularEngravingElement':
    case 'TextEngravingElement':
      return {
        width: element.properties.width.value,
        height: element.properties.height.value
      }
    case 'CircularClippingElement':
    case 'CircularEngravingElement':
      const radius = element.properties.radius.value
      return { width: radius, height: radius }
    default:
      return { width: 0, height: 0 }
  }
}

export const ElementEdgeProperties: IElementEdgeProperties = types
  .model('ElementEdgeProperties', {
    parentReferencePoint: types.optional(ParentReferencePoint, {
      value: centerCenter
    }),
    elementReferencePoint: types.optional(ElementReferencePoint, {
      value: centerCenter
    }),
    offset: types.optional(Offset, {
      x: { value: 0 },
      y: { value: 0 }
    } as any)
  })
  .views((self) => ({
    /** */
    getParentReferencePointCoordinates(
      referencePoint?: ReferencePoint,
      referenceInstance?: Instance<IReference>
    ): [number, number] {
      const edge = getParent<Instance<IElementEdge>>(self)
      const reference = referenceInstance || edge.reference.value
      /** */
      const parentReferencePoint =
        referencePoint || (self.parentReferencePoint.value as ReferencePoint)
      const coverPlateEdge = getParent<Instance<ICoverPlateEdge>>(self, 5)
      const { matrix, position = [0, 0, 0] } = coverPlateEdge.properties
        .translation as Translation
      const positionVector = new THREE.Vector3(...position)
      if (matrix)
        positionVector.applyMatrix4(new THREE.Matrix4().set(...matrix))
      const point = positionVector.toArray() as [number, number, number]
      const side = edge.side
      switch (reference?.__typename) {
        /** Cover Plate */
        case coverPlate:
          const computedOffset = reference.referencePoints[parentReferencePoint]
          return [-computedOffset[2], -computedOffset[0]]
        /** Element Edge */
        case elementEdge:
          const referencePosition = reference.computedOffset
          const [refX, refY] = reference.node.getReferencePoints()[
            parentReferencePoint
          ]
          return [referencePosition.x - refX, referencePosition.y - refY]
        /** Printedcircuitboard */
        case printedcircuitboard:
          const rootOffset = reference.getReferencePoints(side)[centerCenter]
          const originOffset = rootOffset.map((v, i) => v - point[i])
          const referenceOffset = reference.getReferencePoints(side)[
            parentReferencePoint
          ]
          const [oX, oY, oZ] = originOffset
          let [rX, rY, rZ] = referenceOffset as [number, number, number]
          if (edge.side === bottom) rX *= -1
          const [x, y, z] = [oX + rX, oY + rY, oZ + rZ]
          switch (side) {
            case front:
              return [x, y]
            case top:
            case bottom:
              return [-z, -x]
            case right:
              return [-z, y]
            case left:
              return [z, y]
            case back:
              return [-x, y]
          }
      }
      return [0, 0]
    }
  }))
  .views((self) => ({
    get parentReferencePointCoordinates() {
      return self.getParentReferencePointCoordinates()
    },
    get elementReferencePointCoordinates() {
      const edge = getParent<Instance<IElementEdge>>(self)
      return edge.node.getReferencePoints()[self.elementReferencePoint.value]
    }
  }))
  .actions((self) => ({
    patch(
      [change, reverseChange]: [any, any],
      patch: boolean | undefined = undefined,
      path?: string
    ) {
      if (path) {
        return [
          { op: 'replace', path, value: change },
          { op: 'replace', path, value: reverseChange }
        ] as [IJsonPatch, IJsonPatch]
      }
    },
    updateOffset(offset: SnapshotIn<IOffset>) {
      self.offset = offset as any
    }
  }))
  .views((self) => {
    const plateEdge = getParent<Instance<ICoverPlateEdge>>(self, 5)
    return {
      get plateOrigin() {
        const { position = [], matrix } = plateEdge.properties.translation
        const point = new THREE.Vector3(...position)
        matrix && point.applyMatrix4(new THREE.Matrix4().set(...matrix))
        return point
      }
    }
  })
  .extend((self) => {
    const plate = getParent<
      Instance<IPrintedcircuitboard | IAdapterPlate | ICoverPlate>
    >(self, 4)
    const edge = getParent<Instance<IElementEdge>>(self)
    const element = edge.node

    const dimensions = {
      plate: {
        width: plate.properties.width.value,
        height: plate.properties.length.value
      },
      element: { width: 0, height: 0 }
    }

    const elementDimension = getElementSize(element)
    Object.entries(elementDimension).forEach(([key, value]) => {
      if (value instanceof Promise) {
        value.then((n) => {
          dimensions.element[key as 'width' | 'height'] = n
        })
      } else {
        dimensions.element[key as 'width' | 'height'] = value
      }
    })

    return {
      views: {
        get translation() {
          const elementEdge = getParent<Instance<IElementEdge>>(self)
          return {
            position: [
              elementEdge.computedOffset.x,
              elementEdge.computedOffset.y,
              0
            ] as [number, number, number]
          }
        }
      }
    }
  })
